import { FieldDescription } from "@/ds/shadcn/components/ui/field";
import { AuthHeader } from "@/features/auth/components/AuthHeader";
import { AuthHeaderSubtitle } from "@/features/auth/components/AuthHeaderSubtitle";
import { AuthHeaderTitle } from "@/features/auth/components/AuthHeaderTitle";
import { useFocusOnLoad } from "@/lib/common/useFocus";
import { CurrencyOptions, CurrencySchema, type Currency } from "@/lib/domain/Currency";
import { Form } from "@/lib/form/components/Form";
import { useAppForm } from "@/lib/form/useAppForm";
import { useEffect, useMemo, type FC } from "react";
import { Link, useNavigate } from "react-router";
import { z } from "zod";
import { useRegister } from "@/features/auth/api/useRegister";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useTitle } from "@/lib/common/useTitle";
import { SelectItem } from "@/ds/shadcn/components/ui/select";

export const PageRegister: FC = () => {
	useTitle("Create Account");

	const ref = useFocusOnLoad();
	const register = useRegister();
	const navigate = useNavigate();

	const validators = useMemo(() => z.object({
		displayName: z.string().min(2, "Display Name must be at least 2 characters long").max(40, "Display Name must be no more than 40 characters long"),
		emailAddress: z.email("Please supply a valid email address"),
		password: z.string().min(8, "Your password must be at least 8 characters long"),
		confirmPassword: z.string(),
		preferredCurrency: CurrencySchema
	}).refine(x => x.password === x.confirmPassword, {
		message: "Passwords must match",
		path: ["confirmPassword"]
	}), []);

	const form = useAppForm({
		defaultValues: {
			displayName: "",
			emailAddress: "",
			password: "",
			confirmPassword: "",
			preferredCurrency: "USD" as Currency
		},
		validators: {
			onMount: validators,
			onChange: validators
		},
		onSubmit: async ({ value }) => register.mutateAsync({
			displayName: value.displayName,
			emailAddress: value.emailAddress,
			password: value.password,
			preferredCurrency: value.preferredCurrency,
			registerPassword: null,
			attribution: null
		})
	});

	useEffect(() => {
		if (!register.isSuccess) {
			return;
		}

		navigate("../email-verify");
	}, [register.isSuccess, navigate]);

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit();
	};

	return (
		<>
			<AuthHeader>
				<AuthHeaderTitle>Create Account</AuthHeaderTitle>
				<AuthHeaderSubtitle>Fill in the form below to create your account</AuthHeaderSubtitle>
			</AuthHeader>

			<Form onSubmit={onSubmit}>
				<form.AppField name="displayName">
					{
						field => <field.TextField label="Display Name" ref={ref} required />
					}
				</form.AppField>
				<form.AppField name="emailAddress">
					{
						field => <field.TextField label="Email Address" type="email" inputMode="email" required />
					}
				</form.AppField>
				<form.AppField name="password">
					{
						field => <field.TextField label="Password" type="password" required />
					}
				</form.AppField>
				<form.AppField name="confirmPassword">
					{
						field => <field.TextField label="Confirm Password" type="password" required />
					}
				</form.AppField>
				<form.AppField name="preferredCurrency">
					{
						field => (
							<field.SelectField label="Preferred Currency">
								{
									CurrencyOptions.map(x => (
										<SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
									))
								}
							</field.SelectField>
						)
					}
				</form.AppField>
				<ErrorDisplay error={register.error} />
				<form.AppForm>
					<form.SubmitButton label="Create Account" />
				</form.AppForm>
				<FieldDescription className="text-center">
					Already have an account? <Link to=".." className="underline underline-offset-4">Sign in</Link>
				</FieldDescription>
			</Form>
		</>
	);
};
